// EST-FEAT-01/02/03 — Assessment scheduling, capture, and external measurement intake.
// Backend: api-service app/api/v1/endpoints/{assessments,assessment_templates,assessment_scheduling}.py
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';
import { workflowEngineApi } from './workflowEngineApi';

interface ApiResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T;
}

interface ApiListResponse<T> {
  success: boolean;
  message_code: number;
  message: string;
  data: T[];
  total: number;
}

// ── Assessment Templates ─────────────────────────────────────────────────────

export interface FieldValidation {
  allow_null: boolean;
  minimum_value?: number | null;
  maximum_value?: number | null;
  allowed_values: unknown[];
}

export interface SegmentDimension {
  dimension_code: string;
  dimension_name: string;
  data_type: 'DECIMAL' | 'INTEGER';
  uom: string;
  required: boolean;
}

export interface SegmentMeasurementField {
  field_code: string;
  field_name?: string;
  data_type?: string;
  uom?: string;
  usage: 'CAPTURED' | 'DERIVED' | 'CAPTURED_OR_DERIVED';
  required: boolean;
  derivation?: { formula: string; rounding_method: string } | null;
}

export interface SegmentType {
  segment_type_code: string;
  segment_type_name: string;
  dimensions: SegmentDimension[];
  segment_measurement_fields: SegmentMeasurementField[];
  active: boolean;
}

export interface MeasurementField {
  field_code: string;
  field_name: string;
  data_type: string;
  uom?: string;
  requirement_level: 'MANDATORY' | 'OPTIONAL';
  missing_behavior: 'BLOCK' | 'WARNING' | 'USE_DEFAULT' | 'IGNORE';
  default_value?: unknown;
  validation: FieldValidation;
}

export interface AssessmentField {
  field_code: string;
  field_name: string;
  field_group: string;
  field_roles: string[];
  data_type: string;
  uom?: string;
  requirement_level: 'MANDATORY' | 'OPTIONAL';
  missing_behavior: 'BLOCK' | 'WARNING' | 'USE_DEFAULT' | 'IGNORE';
  default_value?: unknown;
  validation: FieldValidation;
}

export interface AssessmentTemplateOut {
  assessment_template_id: string;
  assessment_template_code: string;
  assessment_template_name: string;
  assessment_type: string;
  roof_system?: string | null;
  version: string;
  description: string;
  measurement_fields: MeasurementField[];
  assessment_fields: AssessmentField[];
  segment_types: SegmentType[];
  active: boolean;
}

// ── Assessments ───────────────────────────────────────────────────────────────

export interface Source {
  source_type: 'REPORT' | 'VOICE' | 'MANUAL' | 'DERIVED';
  source_reference?: string | null;
}

export interface SegmentDimensionValue {
  dimension_code: string;
  value: unknown;
  uom?: string | null;
}

export interface SegmentMeasurementValue {
  field_code: string;
  value: unknown;
  uom?: string | null;
  value_origin: 'CAPTURED' | 'DERIVED' | 'CONSOLIDATED' | 'MANUAL_OVERRIDE';
  source: Source;
  confidence: number;
}

export interface Segment {
  segment_code: string;
  segment_type_code: string;
  segment_name?: string | null;
  dimensions: SegmentDimensionValue[];
  segment_measurement_values: SegmentMeasurementValue[];
  /** Which capture route produced this segment — a hand-entered plane and one read off an
   *  uploaded report are both segments, and the difference matters when they disagree.
   *  Absent on segments written before precedence existed. */
  source_type?: 'MANUAL' | 'REPORT' | 'PHOTO' | 'VOICE' | 'NOTE' | null;
}

export interface ConsolidatedValue {
  field_code: string;
  value: unknown;
  uom?: string | null;
  /** 'MANUAL_OVERRIDE' — a human corrected this segment-derived value directly on the Assessment
   *  Summary; see SetMeasurementOverrideInput. Cleared (reverts to computed) on the next
   *  Generate Summary / Regenerate run. */
  value_origin: string;
}

export interface AssessmentFieldValue {
  field_code: string;
  value: unknown;
  uom?: string | null;
  source: Source;
  confidence: number;
}

export interface ValidationMessage {
  validation_code: string;
  validation_stage: 'SEGMENT' | 'CONSOLIDATED' | 'ASSESSMENT';
  severity: 'WARNING' | 'BLOCK';
  segment_code?: string | null;
  field_code?: string | null;
  message: string;
  resolution: string;
}

export interface ValidationSummary {
  status: 'VALID' | 'VALID_WITH_WARNINGS' | 'INVALID';
  blocking_issue_count: number;
  warning_count: number;
  ready_for_requirement_generation: boolean;
}

export interface MeasurementReport {
  s3_url: string;
  filename?: string | null;
  content_type?: string | null;
  uploaded_by?: string | null;
  uploaded_on?: string | null;
  /** 'uploaded' = stored, not yet compiled (nothing parses at upload time any more);
   *  'parsed' = a Generate Summary run has since consumed it. 'pending' only appears on
   *  assessments written by the old enqueue-a-parse-job flow. */
  status: 'uploaded' | 'pending' | 'parsed' | 'failed';
  error?: string | null;
  parsed_at?: string | null;
}

export interface GeneratedSummaryQuality {
  overall_confidence?: number | null;
  fields_with_conflicts: number;
  fields_missing: number;
}

export interface GeneratedSummaryReviewItem {
  field: string;
  /** Present only on a "missing field" entry (roof_pipeline found no evidence for this canonical
   *  field from ANY source) — always the literal "missing"; absent on a genuine conflict, which
   *  carries `values`/`resolution` instead. */
  reason?: string;
  /** Present only on a genuine multi-source conflict — the reconciler's own explanation of which
   *  source won and why. */
  resolution?: string;
  values?: unknown[];
  [key: string]: unknown;
}

export interface GeneratedSummaryRecommendations {
  material_recommendation?: string | null;
  color_recommendation?: string | null;
  manufacturer_recommendation?: string | null;
  permit_notes?: string | null;
  special_notes?: string | null;
}

/** One entry in generated_summary.provenance, keyed by canonical field path (e.g.
 *  "roof.ridge_length_ft", "sections[0].area_sqft") — the reconciliation reasoning agent's
 *  citation for why that value won: which source file/page/line/timestamp it came from and how
 *  it compared to any competing sources. See roof_pipeline/reconcile.py's RECONCILE_SYSTEM_PROMPT
 *  point 5. */
export interface MeasurementProvenance {
  file?: string | null;
  page?: number | null;
  line?: number | null;
  timestamp?: string | null;
  section?: string | null;
  note?: string | null;
  source_type?: string | null;
  why?: string | null;
  fact_ids?: string[];
}

export interface GeneratedSummary {
  status?: string | null;
  /** The plain-text statement that was applied to the assessment (what became its segment/field
   *  values) — kept for traceability. */
  compiled_text?: string | null;
  applied_segment_codes?: string[];
  data: {
    roof?: {
      total_area_sqft?: number;
      pitch?: string;
      stories?: number;
      facets?: number;
      ridge_length_ft?: number;
      valley_length_ft?: number;
      eave_length_ft?: number;
      rake_length_ft?: number;
      hip_length_ft?: number;
      wall_flashing_length_ft?: number;
      step_flashing_length_ft?: number;
      transition_length_ft?: number;
      parapet_wall_length_ft?: number;
      unspecified_length_ft?: number;
      material?: string;
    };
    sections?: Array<Record<string, unknown>>;
    damage?: Array<{ type?: string; severity?: string; location?: string }>;
  };
  quality: GeneratedSummaryQuality;
  review: { required: boolean; items: GeneratedSummaryReviewItem[] };
  provenance: Record<string, MeasurementProvenance>;
  warnings: string[];
  recommendations: GeneratedSummaryRecommendations;
  generated_at?: string | null;
}

// Real stages of the ONE background Generate Summary job (see api-service's summary.py/
// service.py) — reported as the job actually reaches each step, not simulated. Polled via the
// same assessment GET the UI already refetches on an interval, so no separate status query.
export type SummaryGenerationStage = 'QUEUED' | 'PARSING' | 'VALIDATING' | 'COMPOSING' | 'DONE' | 'FAILED';
export interface SummaryGenerationStatus {
  status: SummaryGenerationStage;
  stage_label: string;
  started_at: string;
  updated_at: string;
  error?: string | null;
}

export interface AssessmentOut {
  assessment_id: string;
  tenant_id?: string | null;
  engagement_id: string;
  assessment_template_code: string;
  assessment_template_version: string;
  status: 'draft' | 'validated' | 'completed';
  detailed_measurements_segments: Segment[];
  measurement_values: ConsolidatedValue[];
  /** field_code -> hand-typed value currently applied on top of segment consolidation — see
   *  ConsolidatedValue.value_origin === 'MANUAL_OVERRIDE'. Only present for fields someone has
   *  actually corrected; lets the UI show a "reset to computed" affordance. */
  measurement_overrides: Record<string, unknown>;
  assessment_values: AssessmentFieldValue[];
  validation_summary: ValidationSummary;
  validation_messages: ValidationMessage[];
  automation_results: unknown[];
  is_current_reference: boolean;
  measurement_report?: MeasurementReport | null;
  generated_summary?: GeneratedSummary | null;
  summary_generation?: SummaryGenerationStatus | null;
  created_by?: string | null;
  created_on?: string | null;
  updated_on?: string | null;
}

export interface CreateAssessmentInput {
  engagement_id: string;
  assessment_template_code: string;
  assessment_template_version?: string;
  set_as_current_reference?: boolean;
}

export interface AddSegmentInput {
  assessment_id: string;
  segment_type_code: string;
  segment_name?: string;
  dimensions?: { dimension_code: string; value: unknown; uom?: string }[];
  segment_measurement_values?: {
    field_code: string;
    value: unknown;
    uom?: string;
    source?: Source;
    confidence?: number;
  }[];
}

export interface SetAssessmentFieldInput {
  assessment_id: string;
  field_code: string;
  value: unknown;
  uom?: string;
  source_type?: 'REPORT' | 'VOICE' | 'MANUAL' | 'DERIVED';
  source_reference?: string;
  confidence?: number;
}

export interface SetMeasurementOverrideInput {
  assessment_id: string;
  field_code: string;
  value: unknown;
}

export interface ClearMeasurementOverrideInput {
  assessment_id: string;
  field_code: string;
}

export interface ProcessTextInput {
  assessment_id: string;
  text: string;
  source_type?: 'REPORT' | 'VOICE' | 'MANUAL' | 'DERIVED';
  source_reference?: string;
}

// ── Assessment Scheduling ─────────────────────────────────────────────────────

export interface ScheduleAssessmentParticipantInput {
  user_id?: string;
  /** Keeps a resolved property contact LINKED to its CRM record rather than being sent as a
   *  free-typed name/email. */
  contact_id?: string;
  participant_type: 'internal' | 'external';
  name?: string;
  email?: string;
  phone?: string;
}

/** Why a default participant is on the list — mirrors ParticipantSource on the backend. */
export type ParticipantSource = 'sales_agent' | 'actor' | 'primary_poc' | 'owner_promoted';

export interface ResolvedParticipant {
  participant: ScheduleAssessmentParticipantInput;
  source: ParticipantSource;
  /** Backend-resolved label, set only where the backend knows better than this bundle can —
   *  today a tenant-renamed role name (tenant_roles.name). Prefer it over the local translated
   *  wording when present. */
  source_label?: string | null;
}

/** EST-FR-01-002's defaults, resolved but not yet applied. Produced by the SAME backend resolver
 *  that scheduling itself uses, so what the form shows is what would be booked. */
export interface AssessmentSchedulingDefaults {
  participants: ResolvedParticipant[];
  duration_minutes: number;
  location?: { location_type?: string; address?: string | null } | null;
  /** Non-blocking notes: an owner promoted for a property with no primary contact, or no
   *  reachable contact at all. Blocking belongs to the assessment_data_required gate. */
  warnings: string[];
}

export interface ScheduleAssessmentInput {
  engagement_id: string;
  start_time: string;
  end_time?: string;
  agenda?: string;
  /** THE FINAL participant list. When sent, the backend resolves no defaults — which is what
   *  makes removing an auto-added participant stick instead of the server re-adding them.
   *  Mutually exclusive with additional_participants (the backend rejects both). */
  participants?: ScheduleAssessmentParticipantInput[];
  /** Extra participants ON TOP of the backend-resolved defaults. Used by callers that don't
   *  show the defaults first; the scheduling form sends `participants` instead. */
  additional_participants?: ScheduleAssessmentParticipantInput[];
  /** Pre-due reminder offsets (e.g. ["-1d", "-1h"]) — omit to use the tenant's configured
   *  default (the "reminders" config's default_pre_due_offsets); pass [] for no reminders. */
  reminder_offsets?: string[];
}

export interface RescheduleAssessmentInput {
  meeting_id: string;
  start_time: string;
  end_time: string;
  reason?: string;
}

// Any mutation that can flip assessment.validation_summary.ready_for_requirement_generation
// (adding a segment, editing a mandatory field/measurement, compiling a summary) is what
// satisfies the engagement's `required_measurement` governance gate — but that gate is evaluated
// inside workflowEngineApi's separately-cached transitions query, a different createApi() slice
// with its own tags. Without this bridge, "Complete Assessment" keeps showing its stale blocked
// reason after a save that actually cleared it, until an unrelated refetch happens to land. Same
// cross-slice gap workspaceApiExtensions.ts::invalidateEngagementGates fixes for notes/photos.
const invalidateWorkflowGates = async (
  engagementId: string | undefined,
  queryFulfilled: Promise<{ data: AssessmentOut }>,
  dispatch: (action: unknown) => void,
) => {
  try {
    const { data } = await queryFulfilled;
    const id = engagementId ?? data.engagement_id;
    dispatch(workflowEngineApi.util.invalidateTags([
      { type: 'AvailableTransitions', id },
      { type: 'EngagementWorkflowState', id },
    ]));
  } catch { /* the write itself failed — nothing to refresh */ }
};

export const assessmentsApi = createApi({
  reducerPath: 'assessmentsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['AssessmentTemplate', 'Assessment', 'AssessmentList'],
  endpoints: (builder) => ({
    // Templates
    getAssessmentTemplateByCode: builder.query<AssessmentTemplateOut, { code: string; version?: string }>({
      query: ({ code, version }) => ({ url: `/api/v1/assessment-templates/by-code/${code}`, params: version ? { version } : undefined }),
      transformResponse: (res: ApiResponse<AssessmentTemplateOut>) => res.data,
      providesTags: (_r, _e, arg) => [{ type: 'AssessmentTemplate' as const, id: arg.code }],
    }),
    listAssessmentTemplates: builder.query<AssessmentTemplateOut[], { assessment_type?: string } | void>({
      query: (params) => ({ url: '/api/v1/assessment-templates', params: params ?? undefined }),
      transformResponse: (res: ApiListResponse<AssessmentTemplateOut>) => res.data,
      providesTags: ['AssessmentTemplate'],
    }),

    // Assessments
    createAssessment: builder.mutation<AssessmentOut, CreateAssessmentInput>({
      // CreateAssessmentRequest requires engagement_id in the body too (as well as the URL) —
      // keep it in `body` here, not destructured out, or the backend 422s "Field required".
      query: (body) => ({
        url: `/api/v1/engagements/${body.engagement_id}/assessments`, method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      invalidatesTags: ['AssessmentList'],
    }),
    listEngagementAssessments: builder.query<AssessmentOut[], string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/assessments`,
      transformResponse: (res: ApiListResponse<AssessmentOut>) => res.data,
      providesTags: ['AssessmentList'],
    }),
    getCurrentEngagementAssessment: builder.query<AssessmentOut | null, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/assessments/current`,
      transformResponse: (res: ApiResponse<AssessmentOut | null>) => res.data,
      providesTags: (result) => (result ? [{ type: 'Assessment' as const, id: result.assessment_id }] : ['AssessmentList']),
    }),
    getAssessment: builder.query<AssessmentOut, string>({
      query: (assessmentId) => `/api/v1/assessments/${assessmentId}`,
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      providesTags: (_r, _e, id) => [{ type: 'Assessment' as const, id }],
    }),
    addAssessmentSegment: builder.mutation<AssessmentOut, AddSegmentInput>({
      query: ({ assessment_id, ...body }) => ({
        url: `/api/v1/assessments/${assessment_id}/segments`, method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Assessment' as const, id: arg.assessment_id }],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateWorkflowGates(undefined, queryFulfilled, dispatch),
    }),
    updateAssessmentSegment: builder.mutation<AssessmentOut, AddSegmentInput & { segment_code: string }>({
      query: ({ assessment_id, segment_code, ...body }) => ({
        url: `/api/v1/assessments/${assessment_id}/segments/${segment_code}`, method: 'PATCH', body,
      }),
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Assessment' as const, id: arg.assessment_id }],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateWorkflowGates(undefined, queryFulfilled, dispatch),
    }),
    deleteAssessmentSegment: builder.mutation<AssessmentOut, { assessment_id: string; segment_code: string }>({
      query: ({ assessment_id, segment_code }) => ({
        url: `/api/v1/assessments/${assessment_id}/segments/${segment_code}`, method: 'DELETE',
      }),
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Assessment' as const, id: arg.assessment_id }],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateWorkflowGates(undefined, queryFulfilled, dispatch),
    }),
    setAssessmentField: builder.mutation<AssessmentOut, SetAssessmentFieldInput>({
      query: ({ assessment_id, ...body }) => ({
        url: `/api/v1/assessments/${assessment_id}/fields`, method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Assessment' as const, id: arg.assessment_id }],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateWorkflowGates(undefined, queryFulfilled, dispatch),
    }),
    setMeasurementOverride: builder.mutation<AssessmentOut, SetMeasurementOverrideInput>({
      query: ({ assessment_id, field_code, value }) => ({
        url: `/api/v1/assessments/${assessment_id}/measurement-overrides`, method: 'POST', body: { field_code, value },
      }),
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Assessment' as const, id: arg.assessment_id }],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateWorkflowGates(undefined, queryFulfilled, dispatch),
    }),
    clearMeasurementOverride: builder.mutation<AssessmentOut, ClearMeasurementOverrideInput>({
      query: ({ assessment_id, field_code }) => ({
        url: `/api/v1/assessments/${assessment_id}/measurement-overrides/${field_code}`, method: 'DELETE',
      }),
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Assessment' as const, id: arg.assessment_id }],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateWorkflowGates(undefined, queryFulfilled, dispatch),
    }),
    processAssessmentText: builder.mutation<AssessmentOut, ProcessTextInput>({
      query: ({ assessment_id, ...body }) => ({
        url: `/api/v1/assessments/${assessment_id}/process-text`, method: 'POST', body,
      }),
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => invalidateWorkflowGates(undefined, queryFulfilled, dispatch),
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Assessment' as const, id: arg.assessment_id }],
    }),
    uploadMeasurementReport: builder.mutation<AssessmentOut, { assessment_id: string; file: File }>({
      query: ({ assessment_id, file }) => {
        const form = new FormData();
        form.append('file', file);
        return { url: `/api/v1/assessments/${assessment_id}/measurement-report`, method: 'POST', body: form };
      },
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      invalidatesTags: (_r, _e, arg) => [{ type: 'Assessment' as const, id: arg.assessment_id }],
    }),

    generateAssessmentSummary: builder.mutation<AssessmentOut, string>({
      // Real LLM calls (measurement reconciliation + recommendations) — can take up to a
      // couple minutes for the reasoning-heavy reconciliation step, so no client timeout here
      // beyond baseQuery's default.
      query: (assessmentId) => ({ url: `/api/v1/assessments/${assessmentId}/generate-summary`, method: 'POST' }),
      transformResponse: (res: ApiResponse<AssessmentOut>) => res.data,
      invalidatesTags: (_r, _e, id) => [{ type: 'Assessment' as const, id }],
      onQueryStarted: (_arg, { dispatch, queryFulfilled }) => {
        return invalidateWorkflowGates(undefined, queryFulfilled, dispatch);
      },
    }),

    // Scheduling
    /** Resolved-but-not-applied defaults, so the form can show who will be invited and let the
     *  user drop a row before committing. */
    getAssessmentSchedulingDefaults: builder.query<AssessmentSchedulingDefaults, string>({
      query: (engagementId) => `/api/v1/engagements/${engagementId}/assessment-meetings/defaults`,
      transformResponse: (res: ApiResponse<AssessmentSchedulingDefaults>) => res.data,
    }),
    scheduleAssessmentMeeting: builder.mutation<Record<string, unknown>, ScheduleAssessmentInput>({
      query: ({ engagement_id, ...body }) => ({
        url: `/api/v1/engagements/${engagement_id}/assessment-meetings`, method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<Record<string, unknown>>) => res.data,
    }),
    rescheduleAssessmentMeeting: builder.mutation<Record<string, unknown>, RescheduleAssessmentInput>({
      query: ({ meeting_id, ...body }) => ({
        url: `/api/v1/assessment-meetings/${meeting_id}/reschedule`, method: 'POST', body,
      }),
      transformResponse: (res: ApiResponse<Record<string, unknown>>) => res.data,
    }),
  }),
});

export const {
  useGetAssessmentTemplateByCodeQuery,
  useListAssessmentTemplatesQuery,
  useCreateAssessmentMutation,
  useListEngagementAssessmentsQuery,
  useGetCurrentEngagementAssessmentQuery,
  useGetAssessmentQuery,
  useAddAssessmentSegmentMutation,
  useUpdateAssessmentSegmentMutation,
  useDeleteAssessmentSegmentMutation,
  useSetAssessmentFieldMutation,
  useSetMeasurementOverrideMutation,
  useClearMeasurementOverrideMutation,
  useProcessAssessmentTextMutation,
  useUploadMeasurementReportMutation,
  useGenerateAssessmentSummaryMutation,
  useGetAssessmentSchedulingDefaultsQuery,
  useScheduleAssessmentMeetingMutation,
  useRescheduleAssessmentMeetingMutation,
} = assessmentsApi;
